import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { showToast } from '../lib/toast';
import { getApiErrorMessage } from '../lib/apiErrors';
import type {
  AutomationRuleFilterRequest,
  AutomationRuleLogFilterRequest,
  CreateAutomationRuleRequest,
  UpdateAutomationRuleRequest,
} from '../types/automationRules';

export function useAutomationRules(filters?: AutomationRuleFilterRequest, organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.automationRules.list(filters, organizationId),
    queryFn: async () => {
      const response = await apiService.getAutomationRules(filters, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch automation rules'));
      }
      return response.data || { rules: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
    },
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
  });
}

export function useAutomationRule(id?: string, organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.automationRules.detail(id || ''),
    queryFn: async () => {
      const response = await apiService.getAutomationRule(id!, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch automation rule'));
      }
      return response.data;
    },
    enabled: !!id && !!organizationId,
  });
}

export function useCreateAutomationRule(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateAutomationRuleRequest) => {
      const response = await apiService.createAutomationRule(request, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to create automation rule'));
      }
      return response.data;
    },
    onSuccess: () => {
      showToast.success('Rule created', 'Automation rule created successfully.');
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.lists() });
    },
    onError: (error: Error) => {
      showToast.error('Create failed', error.message || 'Failed to create automation rule.');
    },
  });
}

export function useUpdateAutomationRule(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateAutomationRuleRequest }) => {
      const response = await apiService.updateAutomationRule(id, request, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update automation rule'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      showToast.success('Rule updated', 'Automation rule updated successfully.');
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.detail(data.id) });
    },
    onError: (error: Error) => {
      showToast.error('Update failed', error.message || 'Failed to update automation rule.');
    },
  });
}

export function useDeleteAutomationRule(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.deleteAutomationRule(id, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete automation rule'));
      }
      return id;
    },
    onSuccess: (id) => {
      showToast.success('Rule deleted', 'Automation rule deleted successfully.');
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.lists() });
      queryClient.removeQueries({ queryKey: queryKeys.automationRules.detail(id) });
    },
    onError: (error: Error) => {
      showToast.error('Delete failed', error.message || 'Failed to delete automation rule.');
    },
  });
}

export function useToggleAutomationRule(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isEnabled }: { id: string; isEnabled: boolean }) => {
      const response = await apiService.toggleAutomationRule(id, { isEnabled }, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to toggle automation rule'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      showToast.success(
        data.isEnabled ? 'Rule enabled' : 'Rule disabled',
        `"${data.name}" has been ${data.isEnabled ? 'enabled' : 'disabled'}.`
      );
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.detail(data.id) });
    },
    onError: (error: Error) => {
      showToast.error('Toggle failed', error.message || 'Failed to toggle automation rule.');
    },
  });
}

export function useReorderAutomationRules(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: { ruleId: string; sortOrder: number }[]) => {
      const response = await apiService.reorderAutomationRules(items, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to reorder automation rules'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.lists() });
    },
    onError: (error: Error) => {
      showToast.error('Reorder failed', error.message || 'Failed to reorder automation rules.');
    },
  });
}

export function useSeedDefaultAutomationRules(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.seedDefaultAutomationRules(organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to seed default automation rules'));
      }
      return response.data;
    },
    onSuccess: () => {
      showToast.success('Defaults seeded', 'Default automation rules have been created.');
      queryClient.invalidateQueries({ queryKey: invalidateQueries.automationRules.lists() });
    },
    onError: (error: Error) => {
      showToast.error('Seed failed', error.message || 'Failed to seed default automation rules.');
    },
  });
}

export function useAutomationRuleLogs(ruleId?: string, filters?: AutomationRuleLogFilterRequest, organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.automationRules.logList(ruleId || '', filters),
    queryFn: async () => {
      const response = await apiService.getAutomationRuleLogs(ruleId!, filters, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch automation rule logs'));
      }
      return response.data || { logs: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
    },
    placeholderData: keepPreviousData,
    enabled: !!ruleId && !!organizationId,
  });
}
